import { type ReactNode, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { VIEWER_MODES } from 'constants/all';

import { ViewerHostContractsProvider } from 'modules/common/viewer/hostContracts/ViewerHostContractsProvider';
import type { ViewerAccess } from 'modules/common/viewer/hostContracts/access';
import {
  LOADING,
  locked,
  UNAVAILABLE,
  UNLOCKED,
  type ViewerEditLocks,
} from 'modules/common/viewer/hostContracts/editLocks';

import { getActiveRootFile } from './data/reducer';

const OWNER_ACCESS: ViewerAccess = { relationship: VIEWER_MODES.OWNER };

/**
 * Standalone file viewer composition root.
 *
 * The standalone viewer payload carries the root file's geometry lock (its
 * `quoteContext`). It carries no part or collaboration lock, so those are
 * unavailable: the part stays editable here and the backend enforces its own
 * rules.
 */
export const SfvViewerContractsRoot = ({
  children,
}: {
  children: ReactNode;
}) => {
  const rootFile = useSelector(getActiveRootFile);

  const editLocks = useMemo<ViewerEditLocks>(() => {
    const geometryLockReason = rootFile?.quoteContext?.geometryLockReason;
    return {
      partLock: UNAVAILABLE,
      geometryLock: !rootFile
        ? LOADING
        : geometryLockReason
        ? locked(geometryLockReason)
        : UNLOCKED,
      collaborationLock: UNAVAILABLE,
    };
  }, [rootFile]);

  return (
    <ViewerHostContractsProvider editLocks={editLocks} access={OWNER_ACCESS}>
      {children}
    </ViewerHostContractsProvider>
  );
};
